import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { API_URL } from './api';

const SERVICE_LABELS = {
  lunch: 'Midi',
  dinner: 'Soir',
};

function ReservationPage() {
  const { slug } = useParams();
  const today = useMemo(() => new Intl.DateTimeFormat('sv-SE', { timeZone: 'Europe/Paris' }).format(new Date()), []);
  const reservationApiBase = useMemo(
    () => `${API_URL}/api/reservations${slug ? `/${encodeURIComponent(slug)}` : ''}`,
    [slug],
  );
  const [config, setConfig] = useState(null);
  const [restaurantName, setRestaurantName] = useState('');
  const [dateValue, setDateValue] = useState(today);
  const [partySize, setPartySize] = useState(2);
  const [slots, setSlots] = useState([]);
  const [selectedTime, setSelectedTime] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [notes, setNotes] = useState('');
  const [statusText, setStatusText] = useState('');
  const [loading, setLoading] = useState(false);
  const [slotsLoading, setSlotsLoading] = useState(true);
  const [slotsVersion, setSlotsVersion] = useState(0);
  const submittingRef = useRef(false);
  const requestScopeRef = useRef(reservationApiBase);
  requestScopeRef.current = reservationApiBase;

  useEffect(() => {
    let active = true;
    setConfig(null);
    setRestaurantName('');
    setStatusText('');
    setSelectedTime('');
    setCustomerName('');
    setCustomerPhone('');
    setCustomerEmail('');
    setNotes('');
    fetch(`${reservationApiBase}/public-config`)
      .then(async (res) => {
        const payload = await res.json();
        if (!res.ok || payload?.error) throw new Error(payload?.error || 'Page de réservation indisponible.');
        if (active) {
          setConfig(payload);
          setRestaurantName(payload?.account?.name || '');
        }
      })
      .catch((error) => { if (active) setStatusText(error.message || 'Page de réservation indisponible.'); });
    return () => { active = false; };
  }, [reservationApiBase]);

  useEffect(() => {
    let active = true;
    setSlots([]);
    setSelectedTime('');
    if (!config) { setSlotsLoading(false); return () => { active = false; }; }
    setSlotsLoading(true);
    const url = `${reservationApiBase}/slots?date=${encodeURIComponent(dateValue)}&partySize=${encodeURIComponent(String(partySize))}`;
    fetch(url)
      .then(async (res) => {
        const payload = await res.json();
        if (!res.ok || payload?.error) throw new Error(payload?.error || 'Disponibilités indisponibles.');
        if (active) setSlots(Array.isArray(payload?.slots) ? payload.slots : []);
      })
      .catch((error) => { if (active) setStatusText(error.message || 'Disponibilités indisponibles. Réessayez.'); })
      .finally(() => { if (active) setSlotsLoading(false); });
    return () => { active = false; };
  }, [config, dateValue, partySize, reservationApiBase, slotsVersion]);

  const availableSlots = useMemo(() => slots.filter((slot) => !!slot.available), [slots]);

  const submitReservation = async (event) => {
    event.preventDefault();
    if (submittingRef.current) return;
    if (!config || slotsLoading || !availableSlots.some((slot) => slot.time === selectedTime)) {
      setStatusText('Sélectionne un créneau.');
      return;
    }
    submittingRef.current = true;
    setLoading(true);
    setStatusText('');
    const requestScope = reservationApiBase;
    try {
      const response = await fetch(reservationApiBase, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          reservationDate: dateValue,
          reservationTime: selectedTime,
          partySize,
          customerName,
          customerPhone,
          customerEmail,
          notes,
          service: Number(selectedTime.split(':')[0]) < 16 ? 'lunch' : 'dinner',
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.error || 'Réservation impossible.');
      }
      if (requestScopeRef.current !== requestScope) return;
      setStatusText(`${payload.status === 'confirmed' ? 'Réservation confirmée' : 'Demande de réservation enregistrée, en attente de confirmation'} pour ${payload.reservationDate} à ${payload.reservationTime}.`);
      setSlotsVersion((version) => version + 1);
      setSelectedTime('');
      setNotes('');
    } catch (error) {
      if (requestScopeRef.current === requestScope) {
        setStatusText(error.message || 'Réservation impossible.');
        setSlotsVersion((version) => version + 1);
      }
    } finally {
      submittingRef.current = false;
      setLoading(false);
    }
  };

  return (
    <main className="bc-resa-public-page">
      <section className="bc-resa-public-shell">
        <header className="bc-resa-public-head">
          <span>BECLICK • Réservation</span>
          <h1>{restaurantName ? `Réserver chez ${restaurantName}` : 'Réserver une table'}</h1>
          <p>Choisis une date, un créneau et ton nombre de couverts.</p>
        </header>

        <form onSubmit={submitReservation} className="bc-resa-public-form">
          <div className="bc-resa-public-grid">
            <label className="bc-resa-field">
              Date
              <input type="date" value={dateValue} min={today} onChange={(e) => { setDateValue(e.target.value); setSelectedTime(''); setStatusText(''); }} disabled={loading} required />
            </label>
            <label className="bc-resa-field">
              Couverts
              <input
                type="number"
                min={1}
                max={config?.maxPartySize || 12}
                value={partySize}
                onChange={(e) => { setPartySize(Math.max(1, Number(e.target.value || 1))); setSelectedTime(''); setStatusText(''); }}
                disabled={loading}
                required
              />
            </label>
          </div>

          <div className="bc-resa-slots-card">
            <strong>Créneaux disponibles</strong>
            <div className="bc-resa-slots">
              {availableSlots.map((slot) => (
                <button
                  key={slot.time}
                  type="button"
                  onClick={() => setSelectedTime(slot.time)}
                  disabled={slotsLoading || loading}
                  aria-pressed={selectedTime === slot.time}
                  className={`bc-resa-slot-btn ${selectedTime === slot.time ? 'is-active' : ''}`}
                >
                  {slot.time}
                </button>
              ))}
            </div>
            {slotsLoading ? <p role="status">Chargement des créneaux...</p> : null}
            {!slotsLoading && config && !availableSlots.length ? <p className="bc-resa-public-empty">Aucun créneau disponible pour ce jour.</p> : null}
          </div>

          <div className="bc-resa-public-grid">
            <label className="bc-resa-field">
              Nom
              <input autoComplete="name" maxLength={100} value={customerName} onChange={(e) => setCustomerName(e.target.value)} required />
            </label>
            <label className="bc-resa-field">
              Téléphone
              <input type="tel" autoComplete="tel" maxLength={25} value={customerPhone} onChange={(e) => setCustomerPhone(e.target.value)} required />
            </label>
          </div>

          <div className="bc-resa-public-grid">
            <label className="bc-resa-field">
              Email (optionnel)
              <input type="email" value={customerEmail} onChange={(e) => setCustomerEmail(e.target.value)} />
            </label>
          </div>

          <label className="bc-resa-field">
            Demande spéciale (optionnel)
            <textarea rows={3} maxLength={1000} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </label>

          <div className="bc-resa-public-inline">
            <span className="bc-resa-service-chip">{selectedTime ? SERVICE_LABELS[Number(selectedTime.split(':')[0]) < 16 ? 'lunch' : 'dinner'] : 'Choisissez un créneau'}</span>
          </div>

          <button type="submit" disabled={loading || !config || slotsLoading || !selectedTime} className="bc-resa-btn bc-resa-btn-primary">
            {loading ? 'Envoi...' : 'Demander la réservation'}
          </button>
          {statusText ? <p role="status" className="bc-resa-public-status">{statusText}</p> : null}
        </form>
      </section>
    </main>
  );
}

export default ReservationPage;
